import React, { useRef } from 'react';
import { flushSync } from 'react-dom';
import { BsMoon, BsSun } from 'react-icons/bs';
import { isUserAccountManager, isUserRecruiter, isUserSuperAccountManager } from 'shared-utils';
import { useThemeContext } from 'src/context/ThemeProvider';
import { useUserContext } from 'src/context/UserProvider';
import { UserRole } from 'types';

const ThemeToggleButton = () => {
	const { user } = useUserContext();
	const ref = useRef<HTMLDivElement>(null);
	const { themeMode, setThemeMode } = useThemeContext();

	const toggleDarkMode = async (isDarkMode: boolean) => {
		/**
		 * Return early if View Transition API is not supported
		 * or user prefers reduced motion
		 */
		// console.log({
		// 	ref: !ref.current,
		// 	startViewTransition: !document.startViewTransition,
		// 	window: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
		// });
		if (
			!ref.current ||
			!document.startViewTransition ||
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		) {
			window.localStorage.setItem('theme', JSON.stringify(isDarkMode ? 'dark' : 'light'));
			setThemeMode(isDarkMode ? 'dark' : 'light');
			return;
		}

		await document.startViewTransition(() => {
			flushSync(() => {
				window.localStorage.setItem('theme', JSON.stringify(isDarkMode ? 'dark' : 'light'));
				setThemeMode(isDarkMode ? 'dark' : 'light');
			});
		}).ready;

		const { top, left, width, height } = ref.current.getBoundingClientRect();
		const x = left + width / 2;
		const y = top + height / 2;
		const right = window.innerWidth - left;
		const bottom = window.innerHeight - top;
		const maxRadius = Math.hypot(Math.max(left, right), Math.max(top, bottom));

		document.documentElement.animate(
			{
				clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`],
			},
			{
				duration: 800,
				easing: 'ease-in-out',
				pseudoElement: '::view-transition-new(root)',
			},
		);
	};

	return null;

	return isUserRecruiter(user?.role as UserRole) ||
		isUserAccountManager(user?.role as UserRole) ||
		isUserSuperAccountManager(user?.role as UserRole) ? null : (
		<div ref={ref} className="relative z-[300] w-fit">
			<button
				value="on"
				type="button"
				role="switch"
				aria-checked="false"
				onClick={() => {
					toggleDarkMode(themeMode !== 'dark');
				}}
				data-state={themeMode === 'dark' ? 'checked' : 'unchecked'}
				className="w-[40px] h-[22px] rounded-full bg-gray-200 ring-1 ring-gray-300 dark:bg-gray-800 dark:ring-gray-700 outline-none"
			>
				<span
					data-state={themeMode === 'dark' ? 'checked' : 'unchecked'}
					className="flex items-center justify-center w-[20px] h-[20px] bg-white dark:bg-gray-950 text-gray-600 dark:text-gray-300 shadow rounded-full transition-all translate-x-px data-[state=checked]:translate-x-[19px]"
				>
					{themeMode === 'dark' ? <BsMoon size="11" /> : <BsSun size="11" />}
				</span>
			</button>
		</div>
	);
};

export default ThemeToggleButton;
